import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CandidateTabs } from "../../components/candidate/CandidateTabs";
import { AccountSettings } from "../../components/AccountSettings";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-stone-sub mb-1">{label}</label>
    {children}
  </div>
);

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";

export default function Profile() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const p = user?.profile || {};
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    city: p.city || "",
    state: p.state || "",
    experience_years: p.experience_years ?? "",
    speaks_english: !!p.speaks_english,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/candidate/profile", {
        name: form.name,
        phone: form.phone,
        city: form.city,
        state: form.state,
        experience_years: form.experience_years === "" ? null : Number(form.experience_years),
        speaks_english: form.speaks_english,
      });
      await refresh();
      toast.success(t("candidate.profile.saved"));
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || "");
    toast.success(t("candidate.profile.referralCode"));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("candidate.profile.title")}</h1>
      <CandidateTabs />

      <form onSubmit={save} className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 sm:p-8 space-y-4" data-testid="profile-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("candidate.profile.name")}>
            <input data-testid="profile-name" className={input} value={form.name} onChange={set("name")} />
          </Field>
          <Field label={t("candidate.profile.phone")}>
            <input data-testid="profile-phone" className={input} value={form.phone} onChange={set("phone")} />
          </Field>
          <Field label={t("candidate.profile.city")}>
            <input data-testid="profile-city" className={input} value={form.city} onChange={set("city")} />
          </Field>
          <Field label={t("candidate.profile.state")}>
            <input data-testid="profile-state" className={input} value={form.state} onChange={set("state")} />
          </Field>
          <Field label={t("candidate.profile.experienceYears")}>
            <input data-testid="profile-experience" type="number" min="0" className={input} value={form.experience_years} onChange={set("experience_years")} />
          </Field>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="profile-english" type="checkbox" checked={form.speaks_english} onChange={(e) => setForm((f) => ({ ...f, speaks_english: e.target.checked }))} className="w-5 h-5 accent-[#D95A41]" />
          <span className="text-sm font-medium text-stone-sub">{t("candidate.profile.speaksEnglish")}</span>
        </label>
        <button data-testid="profile-save" type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
          {saving ? "..." : t("candidate.profile.save")}
        </button>
      </form>

      <div className="bg-brand-soft border border-stone-border rounded-2xl p-6 mt-6 flex items-center justify-between gap-4" data-testid="referral-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-stone-text">{t("candidate.profile.referralTitle")}</h3>
            <p className="text-sm text-stone-sub">
              {t("candidate.profile.referralCount")}: <span className="font-semibold">{user?.referral_count ?? 0}</span>
            </p>
          </div>
        </div>
        <button onClick={copyCode} data-testid="referral-copy" className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-stone-border font-semibold text-brand hover:-translate-y-0.5 transition-all">
          <Copy className="w-4 h-4" /> {user?.referral_code}
        </button>
      </div>

      <AccountSettings />
    </div>
  );
}
